import * as sdkVersions from './sdkVersions';
import interceptUndefinedMethod from './intercept';
import ZoomApiError from './ZoomApiError';
import { NativeApis, NativeEvents, Timeouts } from './enums';
import * as types from './ZoomSdkTypes';
import {
  postAppBridgeMessage,
  registerSpecificNotificationHandler,
  unregisterSpecificNotificationHandler,
} from '../../bridge';
import {
  type RequestMessage,
  type NativeAPIResponseMessage,
  ZoomApiResponseError,
} from '@zoom/hybrid-jsbridge';

export default class ZoomAppsSdk {
  private _clientVersion: string | undefined;

  constructor() {
    return interceptUndefinedMethod(this);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.callZoomApi
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async callZoomApi(
    apiName: string,
    data?: types.NativeApiRequestData,
    timeout: number = Timeouts.DEFAULT
  ): Promise<any> {
    const nativeApiRequest: RequestMessage<types.NativeApiRequestData> = {
      apiName,
      data,
      timeout,
    };

    if (apiName !== NativeApis.CONFIG && !isString(this._clientVersion)) {
      throw new Error(
        `must call hybrid.apps.config before using other apps API methods ${apiName}`
      );
    }

    return postAppBridgeMessage(apiName, nativeApiRequest.data, {
      timeout,
      rejectOnErrorResponse: true,
    })
      .then((res) => res)
      .catch((e) => {
        if (e instanceof ZoomApiResponseError) {
          throw new ZoomApiError(e.message, e.code, e.requestId);
        } else {
          throw e;
        }
      });
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.on
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public on(event: `${NativeEvents}`, handler: (data: any) => any): void {
    this.addEventListener(event, handler);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.off
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public off(event: `${NativeEvents}`, handler: (data: any) => any): void {
    this.removeEventListener(event, handler);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.addEventListener
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public addEventListener(
    event: `${NativeEvents}`,
    handler: (data: any) => any
  ): void {
    if (!this._clientVersion) {
      console.warn(
        'Must call hybrid.apps.config before adding an event listener. This will become a thrown error in a future version of the sdk'
      );
    }
    registerSpecificNotificationHandler(event, handler);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.removeEventListener
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public removeEventListener(
    event: `${NativeEvents}`,
    handler: (data: any) => any
  ): void {
    if (!this._clientVersion) {
      console.warn(
        'Must call hybrid.apps.config before adding an event listener. This will become a thrown error in a future version of the sdk'
      );
    }
    unregisterSpecificNotificationHandler(event, handler);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.config
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async config({
    capabilities,
    popoutSize,
    size,
    version = sdkVersions.ZERO_SIXTEEN,
    timeout = Timeouts.DEFAULT,
  }: types.ConfigOptions): Promise<types.ConfigResponse> {
    // Avoid overriding the configuration of appBridge, as it may prevent receiving event responses from appBridge.
    const newOptions: types.NativeConfigOptions = {
      js_api_lists: Array.from(
        new Set([
          ...capabilities,
          'zmAppBridgePub',
          'zmAppBridgeSub',
          'zmOnAppBridgeEvent',
        ])
      ),
      size: popoutSize || size,
      version: version,
    };

    const response = await this.callZoomApi(
      NativeApis.CONFIG,
      newOptions,
      timeout
    );

    if (response.result.clientVersion) {
      this._clientVersion = response.result.clientVersion;
    }

    return response;
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.zmGetUserInfo
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async zmGetUserInfo(): Promise<
    NativeAPIResponseMessage<types.ZMUserInfo>
  > {
    return this.callZoomApi(NativeApis.zmGetUserInfo);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.zmGetClientInfo
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async zmGetClientInfo(): Promise<
    NativeAPIResponseMessage<types.ZMClientInfo>
  > {
    return this.callZoomApi(NativeApis.zmGetClientInfo);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.zmGetAICOption
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async zmGetAICOption(): Promise<
    NativeAPIResponseMessage<types.NativeApiRequestData>
  > {
    return this.callZoomApi(NativeApis.zmGetAICOption);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.zmGetLanguage
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async zmGetLanguage(): Promise<
    NativeAPIResponseMessage<types.NativeApiRequestData>
  > {
    return this.callZoomApi(NativeApis.zmGetLanguage);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.zmOpenSettings
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async zmOpenSettings(): Promise<
    NativeAPIResponseMessage<types.NativeApiRequestData>
  > {
    return this.callZoomApi(NativeApis.zmOpenSettings);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.zmNotifyStaticResourceUpdate
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async zmNotifyStaticResourceUpdate(): Promise<
    NativeAPIResponseMessage<types.GeneralMessage>
  > {
    return this.callZoomApi(NativeApis.zmNotifyStaticResourceUpdate);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.zmGetAuthToken
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async zmGetAuthToken(): Promise<
    NativeAPIResponseMessage<types.ZMGetAuthToken>
  > {
    return this.callZoomApi(NativeApis.zmGetAuthToken);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.zmGetChatInfo
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async zmGetChatInfo(): Promise<
    NativeAPIResponseMessage<types.ZMGetChatInfo>
  > {
    return this.callZoomApi(NativeApis.zmGetChatInfo);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.composeCard
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async composeCard(
    msg: types.ComposeCard
  ): Promise<NativeAPIResponseMessage<types.GeneralMessage>> {
    return this.callZoomApi(NativeApis.composeCard, msg);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.closeModal
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async closeModal(): Promise<
    NativeAPIResponseMessage<types.GeneralMessage>
  > {
    return this.callZoomApi(NativeApis.closeModal);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.getUserContext
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async getUserContext(): Promise<
    NativeAPIResponseMessage<types.UserContext>
  > {
    return this.callZoomApi(NativeApis.getUserContext);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.zmRefreshApp
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async zmRefreshApp(): Promise<
    NativeAPIResponseMessage<types.GeneralMessage>
  > {
    return this.callZoomApi(NativeApis.zmRefreshApp);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.zmOpenUrl
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async zmOpenUrl(
    url: string
  ): Promise<NativeAPIResponseMessage<types.GeneralMessage>> {
    return this.callZoomApi(NativeApis.zmOpenUrl, { url });
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.zmGetMeetingQueryStatus
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async zmGetMeetingQueryStatus(): Promise<
    NativeAPIResponseMessage<types.MeetingQueryStatus>
  > {
    return this.callZoomApi(NativeApis.zmGetMeetingQueryStatus);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.zmSetMeetingQueryPrivilegeSetting
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async zmSetMeetingQueryPrivilegeSetting(
    setting: types.MeetingQueryPrivilegeSetting
  ): Promise<NativeAPIResponseMessage<types.GeneralMessage>> {
    return this.callZoomApi(NativeApis.zmSetMeetingQueryPrivilegeSetting, {
      setting,
    });
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.zmGetMeetingQueryPrivilegeSetting
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async zmGetMeetingQueryPrivilegeSetting(): Promise<
    NativeAPIResponseMessage<types.MeetingQueryPrivilegeSettingResponse>
  > {
    return this.callZoomApi(NativeApis.zmGetMeetingQueryPrivilegeSetting);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.zmGetUserInfoMeetingToken
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async zmGetUserInfoMeetingToken(): Promise<
    NativeAPIResponseMessage<types.UserInfoMeetingToken>
  > {
    return this.callZoomApi(NativeApis.zmGetUserInfoMeetingToken);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.zmGetMeetingSummaryStatus
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async zmGetMeetingSummaryStatus(): Promise<
    NativeAPIResponseMessage<types.MeetingSummaryStatus>
  > {
    return this.callZoomApi(NativeApis.zmGetMeetingSummaryStatus);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.zmGetPAAPLogServiceInfo
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async zmGetPAAPLogServiceInfo(): Promise<
    NativeAPIResponseMessage<types.PAAPLogServiceInfo>
  > {
    return this.callZoomApi(NativeApis.zmGetPAAPLogServiceInfo);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.zmGetMyMeetingSpeakingLanguage
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async zmGetMyMeetingSpeakingLanguage(): Promise<
    NativeAPIResponseMessage<types.MyMeetingSpeakingLanguage>
  > {
    return this.callZoomApi(NativeApis.zmGetMyMeetingSpeakingLanguage);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.zmMyMeetingQueryPrivileges
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async zmMyMeetingQueryPrivileges(): Promise<
    NativeAPIResponseMessage<types.MyMeetingQueryPrivileges>
  > {
    return this.callZoomApi(NativeApis.zmMyMeetingQueryPrivileges);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.zmCommonAicAction
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async zmCommonAicAction(
    action: types.AicAction
  ): Promise<NativeAPIResponseMessage<types.GeneralMessage>> {
    return this.callZoomApi(NativeApis.zmCommonAicAction, action);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.zmGetMeetingUUID
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async zmGetMeetingUUID(
    meeting: types.GetMeetingUUID
  ): Promise<NativeAPIResponseMessage<types.GeneralMessage>> {
    return this.callZoomApi(NativeApis.zmGetMeetingUUID, meeting);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.zmSetCookie
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async zmSetCookie(
    cookie: types.SetCookie
  ): Promise<NativeAPIResponseMessage<types.GeneralMessage>> {
    return this.callZoomApi(NativeApis.zmSetCookie, cookie);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.zmDeleteCookie
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async zmDeleteCookie(
    cookie: types.DeleteCookie
  ): Promise<NativeAPIResponseMessage<types.GeneralMessage>> {
    return this.callZoomApi(NativeApis.zmDeleteCookie, cookie);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.zmGetStaticResourceConfiguration
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async zmGetStaticResourceConfiguration(): Promise<
    NativeAPIResponseMessage<types.GetStaticResourceConfiguration>
  > {
    return this.callZoomApi(NativeApis.zmGetStaticResourceConfiguration);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.zmCommonAicActionForPhone
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public async zmCommonAicActionForPhone(
    action: types.AicActionForPhone
  ): Promise<NativeAPIResponseMessage<types.GeneralMessage>> {
    return this.callZoomApi(NativeApis.zmCommonAicActionForPhone, action);
  }

  /**
   * -------------------------------------------------- API Doc --------------------------------------------------
   * [API]
   * apps.onZmCommonAicActionForPhoneEvent
   *
   * [PLATFORM]
   * mac        5.11.0+
   * windows    5.11.0+
   * linux      5.11.0+
   * ios        5.11.0+
   * android    5.11.0+
   *
   * -------------------------------------------------- API Doc --------------------------------------------------
   */
  public onZmCommonAicActionForPhoneEvent(
    handler: types.GenericEventHandler<types.OnZmCommonAicActionForPhoneEvent>
  ) {
    this.on(NativeEvents.zmCommonAicActionForPhoneEvent, handler);
  }
} //// END OF ZOOMSDK

function isString(v: any): boolean {
  return typeof v === 'string';
}
